/**
 * The tenant's plan: limits with what's used, modules and tiers
 * (GET /entitlements/mine). One cached query shared by the sidebar, the
 * usage card and anything that shows "in your plan / not in your plan".
 * The server enforces all of it; this only shapes what people see.
 */
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import useAuthStore from "@/stores/authStore";

export interface UsageRow {
  used: number;
  limit: number | null;
}
export interface Entitlements {
  planName: string;
  usage: { users: UsageRow; branches: UsageRow; businesses: UsageRow };
  modules: Record<string, boolean | string | undefined>;
  tiers: Record<string, string | undefined>;
}

export const entitlementsKey = ["entitlements", "mine"] as const;

export function useEntitlements() {
  const signedIn = useAuthStore((s) => !!s.user);
  return useQuery<Entitlements>({
    queryKey: entitlementsKey,
    queryFn: async () => (await api.get("/entitlements/mine")).data.data,
    enabled: signedIn,
    staleTime: 60_000,
    retry: 1,
  });
}

/** Is a module on? Unknown (still loading / failed) counts as on — the server decides. */
export function hasModule(ent: Entitlements | undefined, key: string): boolean {
  if (!ent) return true;
  const v = ent.modules?.[key];
  return v !== undefined ? Boolean(v) && v !== "none" : true;
}

/** At (or over) a limit? */
export function atLimit(ent: Entitlements | undefined, key: keyof Entitlements["usage"]): boolean {
  const u = ent?.usage?.[key];
  return !!u && u.limit != null && u.used >= u.limit;
}
